import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Clients from '../../components/Clients';
import TopBar from '../../components/TopBar';
import './Home.css';

const Home = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [activeTab, setActiveTab] = useState('instituciones');

  // Typewriter Hero State
  const [heroWord, setHeroWord] = useState('ingenier(ia)');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);

  // Soporte de navegación por hash al llegar desde otra ruta
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []);

  // Typewriter Loop para el Hero
  useEffect(() => {
    const words = ['ingenier(ia)', 'tecnolog(ia)', 'estrateg(ia)', 'cienc(ia)'];
    const currentWordIndex = loopNum % words.length;
    const fullWord = words[currentWordIndex];

    let timer;
    if (isDeleting) {
      if (heroWord.length > 0) {
        timer = setTimeout(() => {
          setHeroWord(fullWord.substring(0, heroWord.length - 1));
        }, 50);
      } else {
        setIsDeleting(false);
        setLoopNum(prev => prev + 1);
      }
    } else {
      if (heroWord.length < fullWord.length) {
        timer = setTimeout(() => {
          setHeroWord(fullWord.substring(0, heroWord.length + 1));
        }, 90);
      } else {
        timer = setTimeout(() => {
          setIsDeleting(true);
        }, 2500);
      }
    }

    return () => clearTimeout(timer);
  }, [heroWord, isDeleting, loopNum]);

  // Formatear palabra del Hero con paréntesis estilizados (ia)
  const renderFormattedWord = (str) => {
    const parts = [];
    let inBrackets = false;
    let buffer = '';

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (char === '(') {
        if (buffer) parts.push(<span key={`txt-${i}`}>{buffer}</span>);
        buffer = '';
        inBrackets = true;
        parts.push(<span key={`open-${i}`} className="brackets">(</span>);
      } else if (char === ')') {
        if (buffer) {
          parts.push(<span key={`ia-${i}`} className="ia-highlight">{buffer}</span>);
          buffer = '';
        }
        inBrackets = false;
        parts.push(<span key={`close-${i}`} className="brackets">)</span>);
      } else {
        buffer += char;
      }
    }
    if (buffer) {
      if (inBrackets) {
        parts.push(<span key="tail" className="ia-highlight">{buffer}</span>);
      } else {
        parts.push(<span key="tail">{buffer}</span>);
      }
    }

    return parts;
  };

  // IntersectionObserver para actualizar sección activa
  useEffect(() => {
    const sectionIds = ['hero', 'perfiles', 'sistema', 'contratacion', 'contacto'];
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -20% 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    sectionIds.forEach(id => {
      const elem = document.getElementById(id);
      if (elem) observer.observe(elem);
    });

    return () => observer.disconnect();
  }, []);

  // Manejo de navegación suave
  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="home-container">
      {/* Cuadrícula estructural técnica de fondo */}
      <div className="bg-grid"></div>

      {/* Header Sticky Unificado */}
      <TopBar activeNav={activeSection} />

      {/* Indicador Lateral de Progreso (Minimalista) */}
      <div className="progress-nav">
        <div
          className={`progress-dot-wrapper ${activeSection === 'hero' ? 'active' : ''}`}
          onClick={() => scrollTo('hero')}
          title="inicio"
        >
          <div className="progress-dash"></div>
        </div>
        <div
          className={`progress-dot-wrapper ${activeSection === 'perfiles' ? 'active' : ''}`}
          onClick={() => scrollTo('perfiles')}
          title="perfiles"
        >
          <div className="progress-dash"></div>
        </div>
        <div
          className={`progress-dot-wrapper ${activeSection === 'sistema' ? 'active' : ''}`}
          onClick={() => scrollTo('sistema')}
          title="sistema"
        >
          <div className="progress-dash"></div>
        </div>
        <div
          className={`progress-dot-wrapper ${activeSection === 'contratacion' ? 'active' : ''}`}
          onClick={() => scrollTo('contratacion')}
          title="contratación"
        >
          <div className="progress-dash"></div>
        </div>
        <div
          className={`progress-dot-wrapper ${activeSection === 'contacto' ? 'active' : ''}`}
          onClick={() => scrollTo('contacto')}
          title="contacto"
        >
          <div className="progress-dash"></div>
        </div>
      </div>

      {/* 0. Hero Section con Banner de Marcas Destacado */}
      <section className="hero" id="hero">
        <div className="hero-grid">
          <h1 id="hero-main-title">
            <span style={{ whiteSpace: 'nowrap' }}>La </span>
            {renderFormattedWord(heroWord)}
            <span className="blinking-cursor">_</span>
            <br />
            <span style={{ whiteSpace: 'nowrap' }}>de las decisiones</span>
            <br />
            <span style={{ whiteSpace: 'nowrap' }}>complejas.</span>
          </h1>
          <div className="hero-desc-group">
            <p>
              Infraestructura analítica lista para escalar. Reemplazamos la intuición por modelos robustos que aseguran consistencia operativa.
            </p>
            <div className="hero-cta-group">
              <a
                href="#perfiles"
                className="hero-btn"
                onClick={(e) => { e.preventDefault(); scrollTo('perfiles'); }}
              >
                encuentra tu punto de entrada <span className="arrow">↓</span>
              </a>
            </div>
          </div>
        </div>

        {/* Banner de marcas y partners institucionales destacado */}
        <div className="hero-clients-container">
          <Clients />
        </div>
      </section>

      {/* 1. Sección de Perfiles (Tabs Interactivos) */}
      <section className="capabilities" id="perfiles">
        <div className="wrap">
          <div className="section-head">
            <h2 className="block-title">
              ¿dónde está el cuello de botella?<span className="blinking-cursor">_</span>
            </h2>
            <p>
              Elegir un perfil cambia el punto de partida. El método y los estándares de entrega siguen siendo los mismos.
            </p>
          </div>

          <div className="profile-layout">
            {/* Selector de pestañas */}
            <div className="profile-tabs" role="tablist" aria-label="perfiles de cliente">
              <button
                className={`profile-tab ${activeTab === 'instituciones' ? 'active' : ''}`}
                id="tab-instituciones"
                role="tab"
                aria-selected={activeTab === 'instituciones'}
                aria-controls="panel-instituciones"
                onClick={() => setActiveTab('instituciones')}
              >
                <span>01</span>instituciones
              </button>
              <button
                className={`profile-tab ${activeTab === 'brokers' ? 'active' : ''}`}
                id="tab-brokers"
                role="tab"
                aria-selected={activeTab === 'brokers'}
                aria-controls="panel-brokers"
                onClick={() => setActiveTab('brokers')}
              >
                <span>02</span>brokers y plataformas
              </button>
              <button
                className={`profile-tab ${activeTab === 'traders' ? 'active' : ''}`}
                id="tab-traders"
                role="tab"
                aria-selected={activeTab === 'traders'}
                aria-controls="panel-traders"
                onClick={() => setActiveTab('traders')}
              >
                <span>03</span>traders avanzados
              </button>
            </div>

            {/* Paneles de Contenido */}
            <div className="profile-content">
              {activeTab === 'instituciones' && (
                <article
                  className="profile-panel"
                  id="panel-instituciones"
                  role="tabpanel"
                  aria-labelledby="tab-instituciones"
                >
                  <div className="profile-letter">i</div>
                  <span className="stage-label">instituciones y equipos de inversión</span>
                  <h2>pasar de investigar a operar sin perder el control.</h2>
                  <p>
                    Cuando datos, experimentos y despliegues viven en herramientas separadas, cada transición depende de conocimiento difícil de transferir.
                  </p>
                  <div className="profile-facts">
                    <div className="profile-fact">
                      <span>primera entrega</span>
                      <p>Diagnóstico de un flujo de investigación a producción.</p>
                    </div>
                    <div className="profile-fact">
                      <span>evidencia</span>
                      <p>Arquitectura, prioridades y criterios del primer módulo.</p>
                    </div>
                    <div className="profile-fact">
                      <span>cliente responsable</span>
                      <p>Dueño del proceso, accesos y aprobación de producción.</p>
                    </div>
                    <div className="profile-fact">
                      <span>buen encaje</span>
                      <p>Problema identificable y equipo que operará lo entregado.</p>
                    </div>
                  </div>
                </article>
              )}

              {activeTab === 'brokers' && (
                <article
                  className="profile-panel"
                  id="panel-brokers"
                  role="tabpanel"
                  aria-labelledby="tab-brokers"
                >
                  <div className="profile-letter">b</div>
                  <span className="stage-label">brokers y plataformas</span>
                  <h2>hacer que proveedores y controles trabajen como un sistema.</h2>
                  <p>
                    La complejidad aparece en los bordes: estados inconsistentes, dependencias externas y procesos de conciliación que crecen con la operación.
                  </p>
                  <div className="profile-facts">
                    <div className="profile-fact">
                      <span>primera entrega</span>
                      <p>Diagnóstico de una integración y prueba de viabilidad.</p>
                    </div>
                    <div className="profile-fact">
                      <span>evidencia</span>
                      <p>Flujos, fallos relevantes, dependencias y alcance técnico.</p>
                    </div>
                    <div className="profile-fact">
                      <span>cliente responsable</span>
                      <p>Acceso autorizado, interlocutor operativo y proveedores disponibles.</p>
                    </div>
                    <div className="profile-fact">
                      <span>buen encaje</span>
                      <p>Una integración concreta con resultado verificable.</p>
                    </div>
                  </div>
                </article>
              )}

              {activeTab === 'traders' && (
                <article
                  className="profile-panel"
                  id="panel-traders"
                  role="tabpanel"
                  aria-labelledby="tab-traders"
                >
                  <div className="profile-letter">t</div>
                  <span className="stage-label">traders independientes avanzados</span>
                  <h2>convertir scripts útiles en una operación que puedas sostener.</h2>
                  <p>
                    Automatizar no basta. El sistema necesita despliegue reproducible, registros, alertas y una forma segura de responder ante desconexiones.
                  </p>
                  <div className="profile-facts">
                    <div className="profile-fact">
                      <span>primera entrega</span>
                      <p>Revisión técnica de un flujo existente y plan de profesionalización.</p>
                    </div>
                    <div className="profile-fact">
                      <span>evidencia</span>
                      <p>Mapa actual, riesgos y propuesta del primer alcance.</p>
                    </div>
                    <div className="profile-fact">
                      <span>cliente responsable</span>
                      <p>Estrategia definida, cuentas propias y capacidad de operación.</p>
                    </div>
                    <div className="profile-fact">
                      <span>buen encaje</span>
                      <p>Herramientas en uso y presupuesto de ingeniería.</p>
                    </div>
                  </div>
                </article>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Sección "El Sistema Compartido" */}
      <section className="block" id="sistema">
        <div className="wrap">
          <div className="section-head">
            <h2 className="block-title">
              el sistema compartido<span className="blinking-cursor">_</span>
            </h2>
            <p>
              No imponemos una plataforma completa. Construimos el tramo que conecta el problema actual con una operación más trazable.
            </p>
          </div>
          <div className="architecture">
            <article className="architecture-step">
              <code>q_ / 01</code>
              <h3>datos</h3>
              <p>Fuentes, transformaciones y controles del flujo incluido.</p>
            </article>
            <article className="architecture-step">
              <code>r_ / 02</code>
              <h3>investigación</h3>
              <p>Experimentos, versiones y supuestos que pueden repetirse.</p>
            </article>
            <article className="architecture-step">
              <code>t_ / 03</code>
              <h3>ejecución</h3>
              <p>Integraciones, estados y respuestas bajo condiciones acordadas.</p>
            </article>
            <div className="architecture-band">
              ^_ supervisión · registros · alertas · recuperación · documentación
            </div>
          </div>
        </div>
      </section>

      {/* 3. Sección "Una Primera Contratación Concreta" */}
      <section className="capabilities" id="contratacion">
        <div className="wrap">
          <div className="section-head">
            <h2 className="block-title">
              una primera contratación concreta<span className="blinking-cursor">_</span>
            </h2>
            <p>
              El diagnóstico se paga por el trabajo que entrega. La conversación inicial sólo determina si existe un problema que podamos abordar responsablemente.
            </p>
          </div>
          <div className="cap-grid">
            <article className="cap-card">
              <span className="cap-index">01 / INSTITUCIÓN</span>
              <h3>un flujo, de investigación a producción</h3>
              <p>Identificamos dependencias, fallos y decisiones antes de definir el primer módulo.</p>
            </article>
            <article className="cap-card">
              <span className="cap-index">02 / BROKER</span>
              <h3>una integración, puesta a prueba</h3>
              <p>Delimitamos el flujo, sus proveedores y los escenarios que deben verificarse.</p>
            </article>
            <article className="cap-card">
              <span className="cap-index">03 / TRADER AVANZADO</span>
              <h3>un proceso, profesionalizado</h3>
              <p>Transformamos un flujo manual o frágil en una operación documentada.</p>
            </article>
            <article className="cap-card">
              <span className="cap-index">04 / TODOS</span>
              <h3>una salida que el equipo pueda sostener</h3>
              <p>Entregamos decisiones, pruebas, documentación y límites conocidos.</p>
            </article>
          </div>
        </div>
      </section>

      {/* 4. Sección de Cierre / Contacto */}
      <section className="closing-statement" id="contacto">
        <div className="wrap">
          <blockquote>
            tres operaciones distintas. una misma exigencia: saber qué hace el sistema cuando las condiciones cambian.
          </blockquote>
          <p>
            Cuéntanos qué herramientas usas, dónde se rompe el flujo y quién necesita operar lo que construyamos.
          </p>
          <div className="hero-actions">
            <a
              href="mailto:contacto@qrtsolutions.com"
              className="btn-line"
            >
              conversemos sobre tu infraestructura →
            </a>
          </div>
        </div>
      </section>

      {/* Footer Institucional */}
      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-logo">qrt^</span>
            <span className="footer-meta">quantitative research &amp; trading</span>
          </div>
          <div className="footer-links">
            <a href="#hero" onClick={(e) => { e.preventDefault(); scrollTo('hero'); }}>inicio</a>
            <a href="#perfiles" onClick={(e) => { e.preventDefault(); scrollTo('perfiles'); }}>perfiles</a>
            <a href="#sistema" onClick={(e) => { e.preventDefault(); scrollTo('sistema'); }}>sistema</a>
            <Link to="/filosofia">filosofía</Link>
          </div>
          <div className="footer-contact">
            <span className="footer-contact-label">contacto</span>
            <a href="mailto:contacto@qrtsolutions.com" className="footer-contact-email">
              contacto@qrtsolutions.com
            </a>
          </div>
          <p className="footer-copy">
            &copy; {new Date().getFullYear()} QRT Solutions. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;